import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { useResponsive, ResponsiveGrid } from '../BlogUtils/ResponsiveUtils';

interface StatisticsCardProps {
  title: string;
  value: string | number;
  description?: string;
  trend?: 'up' | 'down' | 'neutral';
  icon?: React.ReactNode;
  className?: string;
}

const trendConfig = {
  up: {
    icon: TrendingUp,
    color: 'text-green-400',
    bgColor: 'bg-green-500/10',
  },
  down: {
    icon: TrendingDown,
    color: 'text-red-400',
    bgColor: 'bg-red-500/10',
  },
  neutral: {
    icon: Minus,
    color: 'text-gray-400',
    bgColor: 'bg-gray-500/10',
  },
};

export const StatisticsCard: React.FC<StatisticsCardProps> = ({
  title,
  value,
  description,
  trend,
  icon,
  className = '',
}) => {
  const { isMobile } = useResponsive();
  const trendInfo = trend ? trendConfig[trend] : null;
  const TrendIcon = trendInfo?.icon;

  return (
    <div className={`
      bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-xl 
      border border-white/10 ${isMobile ? 'rounded-xl p-4' : 'rounded-2xl p-6'}
      hover:from-white/10 hover:to-white/15 hover:border-blue-400/30
      transition-all duration-300 ${isMobile ? 'hover:scale-102' : 'hover:scale-105'} hover:shadow-2xl
      ${className}
    `}>
      {/* Header */}
      <div className={`flex items-center justify-between ${isMobile ? 'mb-3' : 'mb-4'}`}>
        <div className={`flex items-center ${isMobile ? 'gap-2' : 'gap-3'}`}>
          {icon && (
            <div className={`${isMobile ? 'p-1.5' : 'p-2'} bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-lg`}>
              {React.cloneElement(icon as React.ReactElement, {
                className: isMobile ? 'w-4 h-4' : 'w-5 h-5'
              })}
            </div>
          )}
          <h3 className={`text-white/70 font-medium ${isMobile ? 'text-xs' : 'text-sm'} uppercase tracking-wide`}>
            {title}
          </h3>
        </div>
        
        {trendInfo && TrendIcon && (
          <div className={`
            flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium
            ${trendInfo.bgColor} ${trendInfo.color}
          `}>
            <TrendIcon className="w-3 h-3" />
          </div>
        )}
      </div>

      {/* Value */}
      <div className={isMobile ? 'mb-2' : 'mb-3'}>
        <div className={`${isMobile ? 'text-2xl' : 'text-3xl md:text-4xl'} font-bold bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent`}>
          {value}
        </div>
      </div>

      {/* Description */}
      {description && (
        <p className={`text-white/60 ${isMobile ? 'text-xs' : 'text-sm'} leading-relaxed`}>
          {description}
        </p>
      )}
    </div>
  );
};

// Grid container for multiple statistics
export const StatisticsGrid: React.FC<{ children: React.ReactNode; className?: string }> = ({ 
  children, 
  className = '' 
}) => {
  const { isMobile } = useResponsive();
  
  return (
    <ResponsiveGrid
      cols={{ xs: 1, sm: 2, lg: 3 }}
      gap={isMobile ? '1rem' : '1.5rem'}
      className={`${isMobile ? 'my-6' : 'my-8'} ${className}`}
    >
      {children}
    </ResponsiveGrid>
  );
};

export default StatisticsCard;